import { AfterContentChecked, Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Select, Store } from '@ngxs/store';
import { Compass, CompassProduct, CompassReviewSubmission, SubmitCompassReview, Tag, UpdateCompassProduct } from 'huddyapi';
import { BehaviorSubject, Observable, Subscription, timer } from 'rxjs';
import { SimpleAutocomplete, SimpleAutocompleteOption, SimpleAutocompleteSelection } from '../../directives/simpleAutocomplete';
import { DesktopErrorHandler } from '../../services/errorHandler.service';

class EditTag {
  text: string;
  priority: string;

  constructor(text: string, priority: string) {
    this.text = text;
    this.priority = priority;
  }

  toTag(): Tag {
    const relativePriority: number = parseFloat(this.priority);
    return new Tag({
      text: this.text.trim(),
      priority: 0,
      relativePriority: isNaN(relativePriority) ? null : relativePriority
    });
  }

  static fromTag(tag: Tag): EditTag {
    return new EditTag(tag.text, tag.relativePriority != null ? tag.relativePriority.toString() : tag.priority?.toString())
  }
}

interface PriorityData {
  priority: number;
  tags: string[];
}

@Component({
  selector: 'compass-review-dialog',
  templateUrl: 'compass-review-dialog.component.html',
  styleUrls: ["./compass-review-dialog.component.scss"]
})
export class CompassReviewDialog implements OnInit, AfterContentChecked, OnDestroy {
  @ViewChild('allTagsPopover', { static: false }) allTagsPopover: ElementRef;

  @Select(state => state.compassState.tags) tags$: Observable<Tag[]>;

  set working(value: boolean) {
    this._working = value;
    this.dialogRef.disableClose = value;
  }

  get working(): boolean {
    return this._working;
  }

  set currentNormalizedTagText(currentNormalizedTagText: string) {
    this._currentNormalizedTagText = currentNormalizedTagText;
    this.updateFilteredTags();
  }

  get currentNormalizedTagText(): string {
    return this._currentNormalizedTagText;
  }

  set sortedTags(tags: Tag[]) {
    this._sortedTags = [...(tags || [])].sort(Tag.compare);
  }

  get sortedTags(): Tag[] {
    return this._sortedTags;
  }

  _working: boolean;
  tags: EditTag[];
  _sortedTags: Tag[];
  tagMap: Map<string, Tag>;
  tagsSubscription: Subscription;
  reason: string;
  categorySlug: string;
  stripeKey: string;
  providerSlug: string;
  url: string;
  source: string;
  sourceSlug: string;
  link: string;
  allTags: Tag[];
  allTagsPriorityList: PriorityData[];
  _currentNormalizedTagText: string;
  shouldAutofocusInput: boolean;
  tagAutocomplete: SimpleAutocomplete<Tag>;
  allTagsShowing: boolean;
  allTagsElem: HTMLElement;
  allTagsPosition: string;
  allTagsArrowPosition: string;

  constructor(private dialogRef: MatDialogRef<CompassReviewDialog>, private store: Store, private errorHandler: DesktopErrorHandler, @Inject(MAT_DIALOG_DATA) public data: CompassReviewDialogData) {
    this.working = false;
    this.allTags = [];
    this.buildAllTagsPriorityList();
    this.sortedTags = [];
    this.shouldAutofocusInput = false;
    this.allTagsShowing = false;
    this.tagAutocomplete = new SimpleAutocomplete<Tag>();
    this.reason = '';
    this.currentNormalizedTagText = '';
    this.tagMap = new Map<string, Tag>();
    this.tags = (data.product?.tags || []).map((tag: Tag): EditTag => EditTag.fromTag(tag));
    this.sortTags();
    this.stripeKey = data.product?.stripeKey || '';
    this.providerSlug = data.product?.providerSlug || '';
    this.source = data.product?.primarySource || '';
    this.sourceSlug = data.product?.primarySourceSlug || '';
    this.link = data.product?.providerLink || '';
    this.updateFilteredTags();
  }

  ngOnInit(): void {
    this.tagsSubscription = this.tags$.subscribe((tags: Tag[]) => {
      this.allTags = tags.sort((a: Tag, b: Tag): number => {
        let comp: number = a.text.toLowerCase().localeCompare(b.text.toLowerCase());
        if(comp != 0) return comp;
        return (b.relativePriority || b.priority || 0) - (a.relativePriority || a.priority || 0);
      });
      this.buildAllTagsPriorityList();
      this.tagMap = new Map<string, Tag>(tags.map((tag: Tag) => [tag.text, tag]));
      this.updateFilteredTags();
    });
  }

  ngAfterContentChecked(): void {
    if(this.allTagsShowing && this.allTagsElem) {
      this.allTagsPosition = this.getAllTagsPosition();
      this.allTagsArrowPosition = this.getAllTagsArrowPosition();
    }
  }

  ngOnDestroy(): void {
    this.tagsSubscription?.unsubscribe();
  }

  buildAllTagsPriorityList(): void {
    let map: Map<number, PriorityData> = new Map<number, PriorityData>();
    this.allTags.forEach((tag: Tag) => {
      let priorityData: PriorityData = map.get(tag.priority);
      if(!priorityData) {
        priorityData = {
          priority: tag.priority,
          tags: []
        };
        map.set(tag.priority, priorityData);
      }
      priorityData.tags.push(tag.text);
    });
    let result: PriorityData[] = Array.from(map.values()).sort((a: PriorityData, b: PriorityData): number => b.priority - a.priority);
    result.forEach((priorityData: PriorityData) => priorityData.tags.sort());
    this.allTagsPriorityList = result;
  }

  isLoading(): boolean {
    return !!this.data.loadingSubject.getValue();
  }

  hasPrimarySource(): boolean {
    return this.source?.trim().length > 0;
  }

  hasTag(tagText: string): boolean {
    return this.tagMap.has((tagText || '').trim());
  }

  tagIsNew(tagText: string): boolean {
    const trimmedText = (tagText || '').trim();
    return trimmedText.length > 0 && !this.hasTag(tagText);
  }

  updateFilteredTags(): void {
    const filteredTags: Tag[] = (this.currentNormalizedTagText || '').length == 0 ? this.allTags : this.allTags.filter((tag: Tag): boolean => this.normalize(tag.text).includes(this.currentNormalizedTagText || ''));
    this.tagAutocomplete.getTabs()[0].setOptions(filteredTags.map((tag: Tag): SimpleAutocompleteOption<Tag> => new SimpleAutocompleteOption<Tag>(tag, tag.text)));
  }

  normalize(text: string): string {
    return (text == null ? '' : text).toLowerCase().replace(/\s/g, '');
  }

  getApproveText(): string {
    return this.data.approve ? (this.data.product ? 'Modify' : 'Approve') : 'Reject';
  }

  textOk(text: string, length: number, required: boolean): boolean {
    const trimmedText: string = (text || '').trim();
    return (!required || trimmedText.length > 0) && (length == null || trimmedText.length <= length);
  }

  addTag(): void {
    this.tags.push(new EditTag('', null));
    this.sortTags();
    this.setShouldAutofocusInput();
  }

  removeTag(sortedIndex: number): void {
    const sortedEditTag: EditTag = EditTag.fromTag(this.sortedTags[sortedIndex]);
    const index: number = this.tags.findIndex((tag: EditTag): boolean => tag.text == sortedEditTag.text && tag.priority == sortedEditTag.priority);
    if(index >= 0) {
      this.tags.splice(index, 1);
      this.sortTags();
    }
  }

  tagSelected(selection: SimpleAutocompleteSelection<Tag>, tagIndex: number): void {
    this.tags[tagIndex].priority = selection.option.value.priority.toString();
  }

  tagNameBlurred(tagIndex: number): void {
    const uiTag: EditTag = this.tags[tagIndex];
    const trimmedText: string = uiTag.text?.trim();
    if(trimmedText.length > 0) {
      const tag: Tag = this.tagMap.get(trimmedText);
      if(tag && !(uiTag.priority?.length > 0))
        uiTag.priority = tag.priority.toString();
    }
  }

  sortTags(): void {
    timer(0).subscribe(() => this.sortedTags = this.tags.map((tag: EditTag): Tag => tag.toTag()));
  }

  autofocusInput(element: ElementRef) {
    if(!this.shouldAutofocusInput)
      return;
    this.shouldAutofocusInput = false;
    timer(0).subscribe(() => {
      element?.nativeElement?.focus();
    });
  }

  setShouldAutofocusInput(): void {
    this.shouldAutofocusInput = true;
  }

  setFilter(text: string): void {
    this.currentNormalizedTagText = this.normalize((text || '').trim());
  }

  tagNamesUnique(): boolean {
    const productTagMap: Map<string, void> = new Map<string, void>();
    return this.tags.findIndex((tag: EditTag): boolean => {
      const trimmedText: string = tag.text.trim();
      if(productTagMap.has(trimmedText))
        return true;
      productTagMap.set(trimmedText);
      return false;
    }) < 0;
  }

  arrayToString(arr: any[]): string {
    return arr.join(', ');
  }

  canSubmit(): boolean {
    return !this.working && (this.data.approve
      ? (!this.isLoading()
        && this.textOk(this.stripeKey, null, true)
        && this.tags && this.tags.length > 0
        && this.sortedTags.findIndex((tag: Tag): boolean => !(this.textOk(tag.text, 127, true) && (this.hasTag(tag.text) || tag.relativePriority != null))) < 0
        && this.tagNamesUnique()
        && this.textOk(this.source, 255, false)
        && (!this.hasPrimarySource() || this.textOk(this.sourceSlug, 32, true))
        && this.textOk(this.link, 255, false)
        && this.textOk(this.providerSlug, 32, true))
      : (!this.reason || this.reason.length <= 255));
  }

  submit(): void {
    if(!this.canSubmit())
      return;
    this.working = true;
    let submission: CompassReviewSubmission = {
      compassId: this.data.compass.id,
      approve: this.data.approve
    }
    if(this.data.approve) {
      submission.product = new CompassProduct({
        id: this.data.product?.id,
        compassId: this.data.compass.id,
        stripeKey: this.stripeKey.trim(),
        tags: this.sortedTags,
        providerSlug: this.providerSlug.trim(),
        primarySource: this.source.trim(),
        primarySourceSlug: this.sourceSlug.trim(),
        providerLink: this.link.trim(),
        longDescription: this.data.compass.longDescription,
        name: this.data.compass.name,
        description: this.data.compass.description
      });
    } else {
      submission.rejectReason = this.reason;
    }
    this.store.dispatch(this.data.product ? new UpdateCompassProduct(submission.product) : new SubmitCompassReview(submission)).subscribe(() => this.dialogRef.close(), (error: any) => {
      this.working = false;
      this.errorHandler.handle(error, 'review');
    });
  }

  getAllTagsPositionInfo(): { position: DOMRect, vMode: number, leftSpace: boolean, rightSpace: boolean } {
    if(!(this.allTagsElem && this.allTagsPopover && this.allTagsPopover.nativeElement))
      return null;
    let vMode: number = 0; //-1 = above, 0 = side, 1 = below
    const position = this.allTagsElem.getBoundingClientRect();
    if(position.y <= 17)
      vMode = 1;
    else if(position.bottom + 17 >= window.innerHeight)
      vMode = -1;
    const popoverSpace: number = this.allTagsPopover.nativeElement.offsetWidth + 20;
    let leftSpace: boolean = position.left - popoverSpace > 0, rightSpace: boolean = position.right + popoverSpace < window.innerWidth;
    if(vMode == 0 && !(leftSpace || rightSpace))
      vMode = (position.top + position.height / 2 > window.innerHeight / 2) ? -1 : 1;
    return { position: position, vMode: vMode, leftSpace: leftSpace, rightSpace: rightSpace };
  }

  getAllTagsArrowPosition(): string {
    let top: string = '0', left: string = '0', transform: string = 'unset';
    const positionInfo: { position: DOMRect, vMode: number, leftSpace: boolean, rightSpace: boolean } = this.getAllTagsPositionInfo();
    if(positionInfo) {
      switch(positionInfo.vMode) {
        case -1:
          top = (positionInfo.position.top - 11).toString() + 'px';
          left = (positionInfo.position.left + positionInfo.position.width / 2 - 9.5).toString() + 'px';
          transform = 'scaleY(-1)';
          break;
        case 0:
          top = (positionInfo.position.top + positionInfo.position.height / 2 - 4).toString() + 'px';
          if(positionInfo.rightSpace) {
            left = (positionInfo.position.right - 1).toString() + 'px';
            transform = 'rotate(-90deg)';
          } else {
            left = (positionInfo.position.left - 17).toString() + 'px';
            transform = 'rotate(90deg)';
          }
          break;
        case 1:
          top = (positionInfo.position.bottom + 4).toString() + 'px';
          left = (positionInfo.position.left + positionInfo.position.width / 2 - 9.5).toString() + 'px';
          break;
      }
    }
    return 'top:' + top + ';left:' + left + ';transform:' + transform + ';';
  }

  getAllTagsPosition(): string {
    let top: string = '0', right: string = 'unset', bottom: string = 'unset', left: string = '0', maxHeight: string = 'unset';
    const positionInfo: { position: DOMRect, vMode: number, leftSpace: boolean, rightSpace: boolean } = this.getAllTagsPositionInfo();
    if(positionInfo) {
      if(positionInfo.vMode == 0) {
        maxHeight = (window.innerHeight - 20).toString() + 'px';
        const normalTop: number = positionInfo.position.top + positionInfo.position.height / 2 - 22;
        top = Math.min(Math.max(10, window.innerHeight - this.allTagsPopover.nativeElement.offsetHeight - 10), normalTop).toString() + 'px';
        if(positionInfo.rightSpace) {
          left = (positionInfo.position.right + 11).toString() + 'px';
        } else {
          left = 'unset';
          right = (window.innerWidth - (positionInfo.position.left - 11)).toString() + 'px';
        }
      } else {
        const normalLeft: number = positionInfo.position.left + positionInfo.position.width / 2 - 22;
        const rightPos: number = positionInfo.position.right - positionInfo.position.width / 2 + 22;
        left = Math.min(Math.max(Math.max(10, rightPos - this.allTagsPopover.nativeElement.offsetWidth), window.innerWidth - this.allTagsPopover.nativeElement.offsetWidth - 10), normalLeft).toString() + 'px';
        if(positionInfo.vMode == -1) {
          top = 'unset';
          const bottomPos: number = positionInfo.position.top - 11;
          bottom = (window.innerHeight - bottomPos).toString() + 'px';
          maxHeight = (bottomPos - 10).toString() + 'px';
        } else {
          const topNum: number = positionInfo.position.bottom + 11;
          top = topNum.toString() + 'px';
          maxHeight = (window.innerHeight - topNum - 10).toString() + 'px';
        }
      }
    }
    return 'top:' + top + ';right:' + right + ';bottom:' + bottom + ';left:' + left + ';--max-height:' + maxHeight + ';';
  }

  showAllTags(event: any) {
    this.allTagsElem = event.target;
    this.allTagsShowing = true;
  }

  hideAllTags() {
    this.allTagsShowing = false;
    this.allTagsElem = null;
  }
}

export interface CompassReviewDialogData {
  approve: boolean;
  compass: Compass,
  product?: CompassProduct;
  loadingSubject: BehaviorSubject<boolean>;
}
